// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import Mousetrap from '../../workspace/shortcuts/Mousetrap';
import measurementsConnect from '../../components/measurementsStore/measurementsConnect';
import { now, noLonger, hasChanged } from '../../utils/react/propsComparisons';
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';
import {
    initialiseUnscale,
    setUnscaledTransitionDestinationStyles,
    cleanupOnUnscaleTransitionEnd,
    immediatelyUnscale,
    getReturnCanvasScrollOnClick,
} from './canvasUnscaleUtils';
import { initialiseScale, setScaledTransitionDestinationStyles, cleanupOnScaleTransitionEnd } from './canvasScaleUtils';

// Selectors
import { isScaledSelector } from '../store/canvasScaleSelector';
import getGridSize from '../../utils/grid/gridSizeSelector';

// Actions
import { getMeasurementsMapThunk } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { canvasUnscale, getCurrentBoardCanvasElementsThunk } from '../store/canvasActions';

// Constants
import { KEY_CODES } from '../../utils/keyboard/keyConstants';

// The time taken for the scale in / out transition. This should match the CSS transition duration.
const TRANSITION_DURATION_MS = 600;

const currentBoardIdHasChanged = hasChanged('currentBoardId');
const nowScaled = now('isScaled');
const noLongerScaled = noLonger('isScaled');

const mapMeasurementsDispatchToProps = (dispatch) => ({
    dispatchGetMeasurementsMap: () => dispatch(getMeasurementsMapThunk()),
});

const mapStateToProps = createStructuredSelector({
    isScaled: isScaledSelector,
    gridSize: getGridSize,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchGetCanvasElements: () => dispatch(getCurrentBoardCanvasElementsThunk()),
    dispatchCanvasUnscale: () => dispatch(canvasUnscale()),
});

@measurementsConnect(null, mapMeasurementsDispatchToProps)
@connect(mapStateToProps, mapDispatchToProps)
class CanvasScaleManager extends React.Component {
    constructor(props) {
        super(props);

        this.currentlyScaled = false;

        this.transitioning = false;
        this.queuedScale = false;
    }

    componentWillReceiveProps(nextProps) {
        if (currentBoardIdHasChanged(this.props, nextProps)) return this.handleImmediateUnscale(nextProps);
        if (nowScaled(this.props, nextProps)) return this.handleScale(nextProps);
        if (noLongerScaled(this.props, nextProps)) return this.handleUnscale(nextProps);
    }

    componentWillUnmount() {
        this.clearTimers();
        this.removeEventListeners();
    }

    onKey = (event) => {
        const { dispatchCanvasUnscale } = this.props;

        const shouldUnscale =
            event.keyCode === KEY_CODES.ESC ||
            event.keyCode === KEY_CODES.DELETE ||
            event.keyCode === KEY_CODES.BACKSPACE ||
            event.keyCode === KEY_CODES.Z;

        if (shouldUnscale) {
            event.preventDefault();
            event.stopPropagation();
            return dispatchCanvasUnscale();
        }
    };

    onClick = (event) => {
        const { dispatchCanvasUnscale } = this.props;
        const workspacePositioner = document.querySelector('.WorkspacePositioner');

        if (workspacePositioner && workspacePositioner.contains(event.target)) {
            event.preventDefault();
            event.stopPropagation();

            this.returnCanvasScroll = getReturnCanvasScrollOnClick(event, {
                ...this.props,
                scaleFactor: this.scaleFactor,
                elementBoundingRect: this.elementBoundingRect,
            });

            dispatchCanvasUnscale();
        }
    };

    clearTimers = () => {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.animationEndTimeoutId) clearTimeout(this.animationEndTimeoutId);
    };

    addEventListeners = () => {
        this.removeEventListeners();

        Mousetrap.pause();

        document.addEventListener('keydown', this.onKey);
        document.addEventListener('click', this.onClick);
    };

    removeEventListeners = () => {
        Mousetrap.resume();

        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('click', this.onClick);
    };

    storeReturnCanvasScroll = () => {
        const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();

        this.returnCanvasScroll = {
            left: canvasViewportNode.scrollLeft,
            top: canvasViewportNode.scrollTop,
        };
    };

    handleScale = (props) => {
        if (!props) return;

        // If we're currently transitioning then don't do anything yet, wait for the scale in to finish
        if (this.transitioning) {
            this.queuedScale = !this.queuedScale;
            return;
        }

        this.queuedScale = false;
        this.transitioning = true;

        this.currentlyScaled = true;

        this.storeReturnCanvasScroll(props);
        this.clearTimers();

        // Prepare the workspace for transitioning
        initialiseScale(props);

        // Begin the transition
        // Delay by an animation frame so the classes have time to apply
        this.rafId = requestAnimationFrame(() => {
            const { scaleFactor, elementBoundingRect } = setScaledTransitionDestinationStyles({
                ...props,
                returnCanvasScroll: this.returnCanvasScroll,
            });

            // Add event listeners after a timeout to ensure the first keypress isn't handled by it too
            this.addEventListeners();

            this.scaleFactor = scaleFactor;
            this.elementBoundingRect = elementBoundingRect;
        });

        // Once the transition has completed, perform a cleanup of styles and classes
        this.animationEndTimeoutId = setTimeout(() => {
            cleanupOnScaleTransitionEnd(props);

            this.transitioning = false;

            // If there's a queued scale, then unscale once we've finished scaling
            if (this.queuedScale) {
                this.queuedScale = false;
                return this.handleUnscale(this.props);
            }
        }, TRANSITION_DURATION_MS);
    };

    handleUnscale = (props) => {
        // If we're currently transitioning then don't do anything yet, wait for the scale to finish
        if (this.transitioning) {
            this.queuedScale = !this.queuedScale;
            return;
        }

        this.queuedScale = false;
        this.transitioning = true;

        this.clearTimers();
        this.removeEventListeners();

        // Prepare the workspace for transitioning
        initialiseUnscale(props);

        // Begin the transition
        // Delay by an animation frame so the classes have time to apply
        this.rafId = requestAnimationFrame(() =>
            setUnscaledTransitionDestinationStyles({
                ...props,
                returnCanvasScroll: this.returnCanvasScroll,
            }),
        );

        // Once the animation has completed, perform a cleanup of styles and classes if required
        this.animationEndTimeoutId = setTimeout(() => {
            cleanupOnUnscaleTransitionEnd({
                ...props,
                returnCanvasScroll: this.returnCanvasScroll,
            });

            this.currentlyScaled = false;
            this.transitioning = false;

            // If there's a queued scale, then scale once we've finished unscaling
            if (this.queuedScale) {
                this.queuedScale = false;
                return this.handleScale(this.props);
            }
        }, TRANSITION_DURATION_MS);
    };

    handleImmediateUnscale = (props) => {
        if (!this.currentlyScaled) return;

        this.clearTimers();
        this.removeEventListeners();

        immediatelyUnscale();
    };

    render() {
        return null;
    }
}

CanvasScaleManager.propTypes = {
    isScaled: PropTypes.bool,
    canvasSize: PropTypes.object,
    gridSize: PropTypes.number,
    currentBoardId: PropTypes.string,

    dispatchGetMeasurementsMap: PropTypes.func,
    dispatchGetCanvasElements: PropTypes.func,
    dispatchCanvasUnscale: PropTypes.func,
};

export default CanvasScaleManager;
